import { config } from '../config';
import { validateAndRefreshToken } from '../utils/tokenUtils';
import { joinUrlPath } from '../utils/joinUrlPath';
import { HTTP_UNAUTHORIZED } from '../constants/botApi';
import { STANDALONE_JOIN_MODE, StandaloneJoinMode, WATCH_REQUEST_TIMEOUT_MS } from '../constants/watchSession';
import type { ApiError } from './apiService';
import type { StandaloneJoin, WatchableSession } from '../types/liveSession';

/**
 * REST calls to the services' standalone session service, the same ones the
 * desktop apps make (macOS `StandaloneSessionService.swift`): list the live
 * desktop calls and join one, as a read-only observer or (D6, 2026-10-05) as
 * a co-seller with their own mic (`mode: "salesperson"`). The service takes the
 * user's Firebase ID token as `?token=` (not a header) and admits any user of
 * this tenant. The base URL is derived from REACT_APP_STANDALONE_WS_URL
 * (config.server.standaloneApiUrl).
 */

/** One row of GET /api/standalone/sessions/active (services `ActiveSessionSummary`). */
interface ActiveStandaloneSessionWire {
  session_id?: string;
  session_name?: string;
  owner_email?: string;
  owner_name?: string | null;
  started_at?: string | null;
  /** The requester already co-sells this call as `salesperson_<n>` (their app crashed): rejoin. */
  joined_as?: string | null;
}

/** POST /api/standalone/sessions/{id}/join (services `CreateSessionResponse`, C1). */
interface StandaloneJoinWire {
  session_id?: string;
  room_id?: string;
  participant_id?: string;
  role?: string;
  capture_mic?: boolean;
  session_name?: string | null;
  owner_email?: string | null;
  owner_name?: string | null;
  started_at?: string | null;
  rejoined?: boolean;
}

export interface StandaloneJoinOptions {
  /** `salesperson` joins with the user's own mic; the default stays the read-only observer. */
  mode?: StandaloneJoinMode;
  /** The name the roster shows when the tenant's user record has none. */
  displayName?: string | null;
}

const toApiError = (status: number, message: string): ApiError => ({
  success: false,
  error: 'STANDALONE_API_ERROR',
  message,
  status,
});

const fetchStandalone = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
  const { token } = await validateAndRefreshToken();
  if (!token) throw toApiError(HTTP_UNAUTHORIZED, 'No Firebase token for the standalone service');

  const url = `${joinUrlPath(config.server.standaloneApiUrl, path)}?token=${encodeURIComponent(token)}`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), WATCH_REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(url, { ...init, signal: controller.signal });
    if (!response.ok) {
      throw toApiError(response.status, `Standalone service answered ${response.status} for ${path}`);
    }
    return (await response.json()) as T;
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Live desktop calls this user can watch. The service leaves out a call the
 * user is already part of (their own included), and any call no desktop is
 * connected to any more.
 */
export const listActiveStandaloneSessions = async (): Promise<WatchableSession[]> => {
  const rows = await fetchStandalone<ActiveStandaloneSessionWire[] | null>('api/standalone/sessions/active');
  return (Array.isArray(rows) ? rows : [])
    .filter((row) => row?.session_id)
    .map((row) => ({
      sessionId: row.session_id as string,
      mode: 'standalone' as const,
      sessionName: row.session_name || '',
      ownerName: row.owner_name || row.owner_email || null,
      startedAt: row.started_at ?? null,
      isOwn: false,
      joinedAs: row.joined_as || null,
    }));
};

/**
 * Join a live desktop call: by default as a read-only observer (no audio, no
 * roster edits; the body stays `{}`, exactly what older apps send), or with
 * `mode: "salesperson"` as a co-seller streaming their own mic. A rejoin by
 * the same user gets the same `salesperson_<n>` back (`rejoined`). Errors keep
 * the HTTP status: 404 not live, 409 your own call, 403 not a tenant member.
 */
export const joinStandaloneSession = async (
  sessionId: string,
  { mode = STANDALONE_JOIN_MODE.observer, displayName = null }: StandaloneJoinOptions = {}
): Promise<StandaloneJoin> => {
  const body = mode === STANDALONE_JOIN_MODE.salesperson ? { display_name: displayName || null, mode } : {};
  const join = await fetchStandalone<StandaloneJoinWire | null>(
    `api/standalone/sessions/${encodeURIComponent(sessionId)}/join`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
  );
  if (!join?.session_id || !join?.participant_id) {
    throw new Error(`The standalone service's join answer for ${sessionId} had no session or participant id`);
  }
  return {
    sessionId: join.session_id,
    roomId: join.room_id || join.session_id,
    participantId: join.participant_id,
    // An older service ignores `mode` and answers as before: an observer.
    role: join.role === STANDALONE_JOIN_MODE.salesperson ? STANDALONE_JOIN_MODE.salesperson : STANDALONE_JOIN_MODE.observer,
    captureMic: join.capture_mic === true,
    sessionName: join.session_name ?? null,
    ownerName: join.owner_name || join.owner_email || null,
    startedAt: join.started_at ?? null,
    rejoined: join.rejoined === true,
  };
};
